import { relativeTime } from "../../i18n/formatters";
import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import { initSocket, disconnectSocket } from '../../utils/socket';

const PAGE_SIZE = 15;

// ── Helpers ───────────────────────────────────────────────────────────────────

const timeAgo = relativeTime;

// ── Component ─────────────────────────────────────────────────────────────────

const NotificationBell = () => {
  useTranslation();
  const auth    = useSelector((state) => state.auth);
  const history = useNavigate();

  const [open,          setOpen]          = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount,   setUnreadCount]   = useState(0);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(false);

  const bellRef  = useRef(null); // bell trigger button
  const panelRef = useRef(null); // portal panel

  const authHeader = useCallback(() => ({
    withCredentials: true,
  }), []);

  // ── Fetch initial unread count ──────────────────────────────────────────
  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get('/api/notifications/unread-count', authHeader());
      setUnreadCount(res.data.count);
    } catch (_) {}
  }, [authHeader]);

  // ── Fetch list when panel opens ─────────────────────────────────────────
  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await axios.get('/api/notifications/list', authHeader());
      setNotifications(res.data.results);
      setUnreadCount(res.data.unread_count);
    } catch (_) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  // ── Socket.IO ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    fetchUnreadCount();
    const socket = initSocket();
    const handleNotification = (notification) => {
      setUnreadCount((c) => c + 1);
      setNotifications((prev) => [notification, ...prev].slice(0, PAGE_SIZE));
    };
    const handleTimerChange = (payload) => {
      window.dispatchEvent(new CustomEvent('minutezilla:timer-changed', { detail: payload }));
    };
    const handleIdleResponse = (payload) => {
      window.dispatchEvent(new CustomEvent('minutezilla:desktop-idle-response', { detail: payload }));
    };
    const handleDesktopActivity = () => {
      window.dispatchEvent(new CustomEvent('minutezilla:desktop-activity'));
    };
    const handleAttendanceChange = (payload) => {
      window.dispatchEvent(new CustomEvent('minutezilla:attendance-changed', { detail: payload }));
    };
    socket.on('notification:new', handleNotification);
    socket.on('timer:changed', handleTimerChange);
    socket.on('desktop:activity', handleDesktopActivity);
    socket.on('desktop:idle-response', handleIdleResponse);
    socket.on('attendance:changed', handleAttendanceChange);
    return () => {
      socket.off('notification:new', handleNotification);
      socket.off('timer:changed', handleTimerChange);
      socket.off('desktop:activity', handleDesktopActivity);
      socket.off('desktop:idle-response', handleIdleResponse);
      socket.off('attendance:changed', handleAttendanceChange);
      disconnectSocket();
    };
  }, [auth.isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Click-outside to close ──────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const inBell  = bellRef.current  && bellRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inBell && !inPanel) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ── Escape key to close ─────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        bellRef.current && bellRef.current.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // ── Actions ─────────────────────────────────────────────────────────────
  const handleBellClick = () => {
    if (!open) fetchList();
    setOpen((o) => !o);
  };

  const markRead = async (id) => {
    try {
      await axios.patch(`/api/notifications/${id}/read`, {}, authHeader());
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (_) {}
  };

  const markAllRead = async () => {
    try {
      await axios.patch('/api/notifications/read-all', {}, authHeader());
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (_) {}
  };

  const clearAll = async () => {
    try {
      await axios.delete('/api/notifications/clear-all', authHeader());
      setNotifications([]);
      setUnreadCount(0);
    } catch (_) {}
  };

  const handleItemClick = (notif) => {
    if (!notif.is_read) markRead(notif.id);
    setOpen(false);
    if (notif.action_url) history(notif.action_url);
  };

  // ── Render ───────────────────────────────────────────────────────────────
  const badgeLabel = unreadCount > 99 ? '99+' : unreadCount;
  const hasNotifs  = notifications.length > 0;

  return (
    <>
      {/* ── Bell trigger ── */}
      <div
        ref={bellRef}
        className='sb-notif-row'
        onClick={handleBellClick}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleBellClick()}
        role='button'
        tabIndex={0}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-haspopup='dialog'
        aria-expanded={open}
      >
        <div className='sb-notif-icon-wrap'>
          <svg
            width='17' height='17'
            viewBox='0 0 24 24'
            fill='none'
            stroke='rgba(255,255,255,0.75)'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
            focusable='false'
          >
            <path d='M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9' />
            <path d='M13.73 21a2 2 0 01-3.46 0' />
          </svg>
          {unreadCount > 0 && (
            <span className='sb-notif-badge' aria-hidden='true'>
              {badgeLabel}
            </span>
          )}
        </div>
        <span className='sb-notif-label'>{tr("Notifications")}</span>
      </div>

      {/* ── Panel (portal → document.body) ── */}
      {open && ReactDOM.createPortal(
        <div
          ref={panelRef}
          className='sb-panel sb-panel--notifications'
          role='dialog'
          aria-modal='true'
          aria-label={tr("Notifications")}
        >
          {/* Header */}
          <div className='sb-panel__header sb-notif-header'>
            <span className='sb-panel__title'>{tr("Notifications")} {unreadCount > 0 && (
                <span className='sb-notif-header__count' aria-hidden='true'>
                  {unreadCount}
                </span>
              )}
            </span>

            <div className='sb-notif-header__actions'>
              {unreadCount > 0 && (
                <button
                  className='sb-notif-action-btn'
                  onClick={markAllRead}
                  aria-label={tr("Mark all notifications as read")}
                >{tr("Mark read")} </button>
              )}
              {hasNotifs && (
                <button
                  className='sb-notif-action-btn sb-notif-action-btn--clear'
                  onClick={clearAll}
                  aria-label={tr("Clear all notifications")}
                >{tr("Clear all")} </button>
              )}
              <button
                className='sb-notif-close'
                onClick={() => setOpen(false)}
                aria-label={tr("Close notifications")}
              >
                <svg width='12' height='12' viewBox='0 0 12 12' fill='none' aria-hidden='true' focusable='false'>
                  <path d='M1 1l10 10M11 1L1 11' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round'/>
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <div
            className='sb-notif-list'
            role='list'
            aria-live='polite'
            aria-label={tr("Notification items")}
          >
            {loading && (
              <div className='sb-notif-empty' aria-busy='true' aria-label={tr("Loading notifications")}>
                <div className='sb-notif-spinner' />
                <p>{tr("Loading…")}</p>
              </div>
            )}

            {!loading && error && (
              <div className='sb-notif-empty' role='alert'>
                <p style={{ color: '#e53e3e' }}>{tr("Failed to load. Please try again.")}</p>
              </div>
            )}

            {!loading && !error && !hasNotifs && (
              <div className='sb-notif-empty'>
                <svg
                  width='36' height='36'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='#cbd5e0'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                  focusable='false'
                >
                  <path d='M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9' />
                  <path d='M13.73 21a2 2 0 01-3.46 0' />
                </svg>
                <p>{tr("You're all caught up!")}</p>
              </div>
            )}

            {!loading && !error && notifications.map((n) => (
              <div
                key={n.id}
                role='listitem'
                className={`sb-notif-item${!n.is_read ? ' sb-notif-item--unread' : ''}`}
                onClick={() => handleItemClick(n)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleItemClick(n)}
                tabIndex={0}
                aria-label={`${n.title}${!n.is_read ? tr(', unread') : ''}. ${n.message}. ${timeAgo(n.created_at)}.${n.action_url ? tr(' Press Enter to navigate.') : ''}`}
              >
                <div className='sb-notif-item__content'>
                  <div className='sb-notif-item__title' aria-hidden='true'>{n.title}</div>
                  <div className='sb-notif-item__msg'   aria-hidden='true'>{n.message}</div>
                  <div className='sb-notif-item__time'  aria-hidden='true'>{timeAgo(n.created_at)}</div>
                </div>
                {!n.is_read && (
                  <span className='sb-notif-item__dot' aria-hidden='true' />
                )}
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default NotificationBell;
